import React, { useState } from "react";
import { Mail, MessageSquare, Globe, MapPin, Send, CheckCircle2 } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

export default function ContactPage() {
  const { t } = useI18n();
  const [f, setF] = useState({ name: "", email: "", subject: "algemeen", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) return toast.error(t("contact.error_fields"));
    setBusy(true);
    try { await http.post("/contact", f); setSent(true); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <section className="wv-hero-grain bg-gradient-to-b from-[#F8F9FA] to-white py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">{t("contact.kicker")}</p>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2" data-testid="contact-title">{t("contact.title")}</h1>
            <p className="mt-3 text-slate-600 max-w-xl">{t("contact.subtitle")}</p>
            <div className="mt-6 space-y-3">
              <a href="mailto:info@woonverder.eu" className="flex items-center gap-3 p-4 rounded-xl bg-white border border-slate-200 hover:border-[color:var(--wv-green)]" data-testid="contact-email">
                <Mail className="w-5 h-5 text-[color:var(--wv-green)]" />
                <div><p className="text-xs uppercase font-bold text-slate-500">{t("contact.email_label")}</p><p className="font-semibold">info@woonverder.eu</p></div>
              </a>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-white border border-slate-200">
                <MapPin className="w-5 h-5 text-[color:var(--wv-green)]" />
                <div><p className="text-xs uppercase font-bold text-slate-500">{t("contact.region_label")}</p><p className="font-semibold">{t("contact.region_value")}</p></div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-white border border-slate-200">
                <Globe className="w-5 h-5 text-[color:var(--wv-green)]" />
                <div><p className="text-xs uppercase font-bold text-slate-500">{t("contact.languages_label")}</p><p className="font-semibold">NL · EN · DE · ES</p></div>
              </div>
            </div>
          </div>
          {!sent ? (
            <form onSubmit={submit} className="p-6 rounded-2xl bg-white border border-slate-200 space-y-4">
              <p className="font-display text-2xl font-bold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[color:var(--wv-green)]" /> {t("contact.form_title")}</p>
              <div><label className="text-sm font-semibold">{t("contact.name")}</label><input className="wv-input mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" required /></div>
              <div><label className="text-sm font-semibold">{t("contact.email_field")}</label><input className="wv-input mt-1" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email-input" required /></div>
              <div><label className="text-sm font-semibold">{t("contact.subject")}</label>
                <select className="wv-input mt-1" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="contact-subject">
                  <option value="algemeen">{t("contact.subj_general")}</option>
                  <option value="verkoop">{t("contact.subj_seller")}</option>
                  <option value="zoeken">{t("contact.subj_buyer")}</option>
                  <option value="fotografie">{t("contact.subj_photo")}</option>
                  <option value="privacy">{t("contact.subj_privacy")}</option>
                  <option value="klacht">{t("contact.subj_complaint")}</option>
                </select>
              </div>
              <div><label className="text-sm font-semibold">{t("contact.message")}</label><textarea className="wv-input mt-1" rows="5" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" required /></div>
              <button type="submit" className="wv-btn-green w-full inline-flex items-center justify-center gap-2" disabled={busy} data-testid="contact-submit"><Send className="w-4 h-4" /> {busy ? t("contact.sending") : t("contact.submit")}</button>
              <p className="text-xs text-slate-500">{t("contact.privacy_note_prefix")} <a href="/privacy" className="underline">{t("authgate.privacy")}</a>.</p>
            </form>
          ) : (
            <div className="p-8 rounded-2xl bg-white border border-slate-200 text-center">
              <CheckCircle2 className="w-14 h-14 text-[color:var(--wv-green)] mx-auto" />
              <p className="font-display text-2xl font-bold mt-4">{t("contact.sent_title")}</p>
              <p className="mt-2 text-slate-600">{t("contact.sent_body")}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
